import { DateUtils, PersonDoc, SubmittalRevision, WorkflowState, WorkflowStateComplete } from 'common';
import { TimelineBubble } from '../models/Timeline';
import { Calendar } from '../../components';
import { h } from '@stencil/core';

/**
 * Construction of TimelineBubble presentation-model objects for the timeline UI.
 * Shared by both submittals and RFIs.
 */
export class TimelineUtils {
  /**
   * Builds timeline bubbles from a raw history array, workflow states, people, and a calendar.
   * Used by both submittals (via getTimelineBubblesFromRevision) and RFIs directly.
   *
   * @param history - The WorkflowStateComplete[] status history array
   * @param states - The workflow states for this document
   * @param people - People to resolve user_id references
   * @param calendar - Calendar instance for computing working-day due dates on pending states
   * @param createStatusId - The fallback id to use when a reversion history item has a null state_id
   */
  static getTimelineBubbles(
    history: WorkflowStateComplete[],
    states: WorkflowState[],
    people: PersonDoc[],
    calendar: Calendar,
    createStatusId: string = 'created',
  ): TimelineBubble[] {
    const lastHistoryItem = history.length > 0 ? history[history.length - 1] : null;
    const lastStateIndex = states.findIndex(s => s.id === lastHistoryItem?.state_id);

    // --- Completed (history) bubbles ---
    const historyBubbles: TimelineBubble[] = history.map(historyItem => {
      const doneDate = DateUtils.fromISO(historyItem.date);
      const user = people.find(p => p._id === historyItem.user_id);

      if (historyItem.previous_state_id) {
        // Reversion bubble: workflow was sent back to a prior state
        const prevState = states.find(s => s.id === historyItem.previous_state_id);
        const returnStateId = historyItem.state_id === null ? createStatusId : historyItem.state_id;
        const returnBubble: TimelineBubble = {
          id: returnStateId,
          dueDate: doneDate,
          doneDate: doneDate,
          people: user ? [user] : [],
          label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {prevState?.action}
              <ion-icon name="arrow-back-outline"></ion-icon>
            </div>
          ),
        };
        return returnBubble;
      }

      const stateIndex = states.findIndex(s => s.id === historyItem.state_id);
      const currentState = states[stateIndex];

      const bubble: TimelineBubble = {
        id: historyItem.state_id,
        dueDate: doneDate,
        doneDate: doneDate,
        people: user ? [user] : [],
        label: currentState?.pastTense ?? currentState?.action ?? '',
      };
      return bubble;
    });

    // --- Pending (future) bubbles, using working-day calendar ---
    const lastHistoryDate = DateUtils.fromISO(lastHistoryItem?.date ?? new Date().toISOString());
    let cumulativeDays = 0;
    const pendingBubbles: TimelineBubble[] = states.slice(lastStateIndex + 1).map(state => {
      const users = people.filter(p => state.dueBy?.includes(p._id));
      cumulativeDays += state.dueDays || 0;
      const durationInSeconds = calendar.workingDaysToSeconds(cumulativeDays);
      const dueDate = calendar.getDateFromPositiveDuration(lastHistoryDate, durationInSeconds);
      return {
        id: state.id,
        dueDate,
        people: users || [],
        label: state.action,
      };
    });

    return [...historyBubbles, ...pendingBubbles];
  }

  /**
   * Convenience wrapper for submittals: extracts history from a SubmittalRevision.
   */
  static getTimelineBubblesFromRevision(currentRevision: SubmittalRevision, states: WorkflowState[], people: PersonDoc[], calendar: Calendar): TimelineBubble[] {
    const history = currentRevision.status_history || [];
    return TimelineUtils.getTimelineBubbles(history, states, people, calendar, 'create');
  }
}
